<template>
    <div class="ScenarioTree">
        <v-layout class="toolbar">
            <v-flex xs2 class="spreadY centerXY">
                <v-btn icon @click="openAllTreeNode" :title="this.$f.tc('expandAll')">
                    <v-icon class="mx-1">mdi-arrow-expand-vertical</v-icon>
                </v-btn>
            </v-flex>
            <v-flex xs2 class="spreadY centerXY">
                <v-btn icon @click="closeAllTreeNode" :title="this.$f.tc('collapseAll')">
                    <v-icon class="mx-1">mdi-arrow-collapse-vertical</v-icon>
                </v-btn>
            </v-flex>
            <v-flex xs2 class="spreadY centerXY" style="border-right: 1px solid #E9EBF7; cursor:pointer;">
                <v-btn icon @click="scrollToNode" :title="this.$f.tc('revealInTree')">
                    <v-icon class="mx-1">mdi-crosshairs-gps</v-icon>
                </v-btn>
            </v-flex>
            <v-flex xs6 class="spreadY" style="cursor:pointer;">
                <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                        <v-layout align-center class="spreadY" v-on="on">
                            <v-icon class="mx-1">mdi-{{ TreeSortTypeIcon[sortType] }}</v-icon>
                            {{ sortText }}
                            <v-flex></v-flex>
                            <v-icon>mdi-menu-down</v-icon>
                        </v-layout>
                    </template>
                    <div class="fci-list">
                        <div
                            v-for="sort in treeSortList"
                            :key="sort.type"
                            class="fci-list-item"
                            :class="{active: sortType === sort.type}"
                            @click="sortType = sort.type; sortText = sort.text"
                        >
                            <v-icon small class="mx-1">mdi-{{ TreeSortTypeIcon[sort.type] }}</v-icon>
                            {{ sort.text }}
                        </div>
                    </div>
                </v-menu>
            </v-flex>
        </v-layout>
        <div class="scenario-list fci-scrollbar">
            <transition-group>
                <div v-for="scenario in scenarioList" :key="scenario.code" class="fci-transition">
                    <div
                        class="item item-scenario"
                        :class="{active: scenario.code === activeScenarioCode, 'drop-zone': scenario.code === dropCode}"
                        @click="selectScenario(scenario)"
                        :draggable="sortType === TreeSortType.CUSTOM"
                        @dragstart="onDragStart($event, scenario, true)"
                        @dragenter="onDragEnter($event, scenario.code, true)"
                        @dragend="onDragEnd($event, scenario.code, true)"
                        @dragover.prevent
                        @drop="onDropScenario($event, scenario)"
                    >
                        <div class="text-ellipsis" :title="scenario.name">{{ scenario.name }}</div>
                        <v-flex></v-flex>
                        <v-icon
                            @click.stop="toggleScenario(scenario)"
                            :class="{active: !!scenarioOpenMap[scenario.code]}"
                        >mdi-chevron-down</v-icon>
                    </div>
                    <div
                        class="step-list"
                        :style="`height: ${getStepListHeight(scenario)}`"
                    >
                        <transition-group>
                            <template v-for="step in scenario.steps">
                                <div
                                    v-if="step.code !== reminderStepCode || allowReminder"
                                    :key="step.code"
                                    @click="selectStep(step)"
                                    class="item item-step"
                                    :class="{active: step.code === activeStepCode, 'drop-zone': step.code === dropCode}"
                                    :draggable="sortType === TreeSortType.CUSTOM && scenario.code !== defaultScenarioCode"
                                    @dragstart="onDragStart($event, step, false)"
                                    @dragenter="onDragEnter($event, step.code, false)"
                                    @dragend="onDragEnd($event, step.code, false)"
                                    @drop="onDropStep($event, step)"
                                    @dragover.prevent
                                    :ref="step.code"
                                >
                                    <div style="overflow:hidden; flex: 1">
                                        <div class="text-ellipsis" :title="step.name">{{ step.name }}</div>
                                        <div style="font-size: 0.75em; color: #a9a9a9;">
                                            {{ getStepCondition(step) }}
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <div
                                key="btn-add"
                                v-if="scenario.code !== defaultScenarioCode"
                                class="item item-step"
                                :class="{drop: (scenario.code + '-step') === dropCode}"
                                @dragenter="onDragEnter($event, scenario.code + '-step', false)"
                                @drop="onDropStep($event, scenario.code + '-step')"
                                @dragover.prevent
                            >
                                <v-btn outlined small color="primary" @click="createStep(scenario)"
                                    style="border-color: #0052CC">
                                    {{ $f.tc('addStep') }}
                                </v-btn>
                            </div>
                        </transition-group>
                    </div>
                </div>
            </transition-group>
        </div>
        <div class="add-wrapper">
            <v-btn color="primary" style="width: 100%" @click="createScenario">
                {{ $f.tc('addScenario') }}
            </v-btn>
        </div>
    </div>
</template>

<script>
import lodash from 'lodash'
import { TreeSortType, TreeSortTypeIcon } from '@/utils/const'
import { mapState } from 'vuex'
import { AppEventBusType } from '@/eventbus/app.eventbus'

const itemIndexAsPosition = function (item, i) {
    item.position = i
}

export default {
    name: 'ScenarioTree',
    data () {
        return {
            TreeSortType,
            TreeSortTypeIcon,
            scenarioOpenMap: {},
            dropCode: null,
            dragItem: null,
            isDragScenario: true,
            scenarioList: [],
            sortType: TreeSortType.CUSTOM,
            sortText: this.$f.tc('custom'),
            isOpenAll: true
        }
    },
    computed: {
        ...mapState({
            scenarios: state => state.scenario.scenarioList,
            mapCodeToScenario: state => state.scenario.mapCodeToScenario,
            mapCodeToStep: state => state.scenario.mapCodeToStep,
            defaultScenarioCode: state => state.scenario.defaultScenarioCode,
            reminderStepCode: state => state.scenario.reminderStepCode,
            allowReminder: state => state.scenario.allowReminder,
            shouldUpdateGraph: state => state.scenario.shouldUpdateGraph,
            activeScenarioCode: state => state.scenario.activeScenarioCode,
            activeStepCode: state => state.scenario.activeStepCode,
            activeScenario: state => state.scenario.activeScenario,
            activeStep: state => state.scenario.activeStep
        }),
        treeSortList () {
            return [
                {
                    text: 'a -> z',
                    type: TreeSortType.NAME_ASC
                },
                {
                    text: 'z -> a',
                    type: TreeSortType.NAME_DSC
                },
                {
                    text: this.$f.tc('timeNewest'),
                    type: TreeSortType.TIME_ASC
                },
                {
                    text: this.$f.tc('timeOldest'),
                    type: TreeSortType.TIME_DSC
                },
                {
                    text: this.$f.tc('custom'),
                    type: TreeSortType.CUSTOM
                }
            ]
        }
    },
    methods: {
        openAllTreeNode () {
            this.openAll(true)
        },
        closeAllTreeNode () {
            this.openAll(false)
        },
        scrollToNode () {
            let activeStep = this.mapCodeToStep[this.activeStepCode]
            this.scenarioOpenMap[activeStep.scenario_code] = true
            this.$forceUpdate()
            this.$nextTick(() => {
                this.$refs[activeStep.code][0].scrollIntoView()
            })
        },
        openAll (isOpen) {
            this.isOpenAll = isOpen
            this.scenarioList.forEach(scenario => {
                this.scenarioOpenMap[scenario.code] = isOpen
            })
            this.$forceUpdate()
        },
        toggleScenario (scenario) {
            this.scenarioOpenMap[scenario.code] = !this.scenarioOpenMap[scenario.code]
            this.$forceUpdate()
        },
        getStepListHeight (scenario) {
            if (!this.scenarioOpenMap[scenario.code]) {
                return 0
            }
            let numberOfStep = 0
            if (scenario.code === this.defaultScenarioCode) {
                numberOfStep = scenario.steps.length
                if (!this.allowReminder) {
                    numberOfStep -= 1
                }
            } else {
                numberOfStep = scenario.steps.length + 1
            }
            return numberOfStep * 43 + 'px'
        },
        selectScenario (scenario) {
            this.scenarioOpenMap[scenario.code] = true
            this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                activeScenarioCode: scenario.code
            })
        },
        selectStep (step) {
            this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                activeStepCode: step.code
            })
        },
        onDragStart (event, scenario, isDragScenario) {
            this.dragItem = scenario
            this.isDragScenario = isDragScenario
        },
        onDragEnter (event, code, isDragScenario) {
            if (!isDragScenario) {
                if (code.split('-').length < 2) {
                    let dropStep = this.mapCodeToStep[code.split('-')[0]]
                    if (dropStep.scenario_code === this.defaultScenarioCode) {
                        return
                    }
                }
            }
            if (this.isDragScenario === isDragScenario) {
                this.dropCode = code
            }
        },
        onDragEnd () {
            this.dropCode = null
        },
        onDropScenario (event, dropScenario) {
            this.dropCode = null
            if (!this.isDragScenario) {
                return
            }
            let dragScenario = this.dragItem
            if (dragScenario.position === dropScenario.position) {
                return
            }
            let start = this.scenarioList.indexOf(dragScenario)
            this.scenarioList.splice(start, 1)
            start = this.scenarioList.indexOf(dropScenario)
            if (dragScenario.position < dropScenario.position) {
                start++
            }
            this.scenarioList.splice(start, 0, dragScenario)
            this.scenarioList.forEach(itemIndexAsPosition)
            this.$forceUpdate()
            this.$store.dispatch(this.$at.scenario.POSITION_SCENARIO, {
                botCode: this.$route.params.botCode,
                scenarioCodeList: this.scenarioList.map(scenario => scenario.code)
            })
        },
        onDropStep (event, dropStep) {
            this.dropCode = null
            if (this.isDragScenario) {
                return
            }
            let dragStep = this.dragItem
            let dragScenario = this.mapCodeToScenario[dragStep.scenario_code]
            let dropScenario = null
            if (typeof dropStep === 'string') {
                // drag step drop add button
                let start = dragScenario.steps.indexOf(dragStep)
                dragScenario.steps.splice(start, 1)
                let dropScenarioCode = dropStep.split('-')[0]
                dropScenario = this.mapCodeToScenario[dropScenarioCode]
                dropScenario.steps.push(dragStep)
            } else {
                // drag step drop step
                if (dropStep.scenario_code === this.defaultScenarioCode) {
                    return
                }
                if (
                    dragStep.scenario_code === dropStep.scenario_code &&
                    dragStep.position === dropStep.position
                ) {
                    return
                }
                // remove from drag scenario
                let dragScenario = this.mapCodeToScenario[dragStep.scenario_code]
                let start = dragScenario.steps.indexOf(dragStep)
                dragScenario.steps.splice(start, 1)
                // insert to drop scenario
                dropScenario = this.mapCodeToScenario[dropStep.scenario_code]
                start = dropScenario.steps.indexOf(dropStep)
                if (dragStep.scenario_code === dropStep.scenario_code) {
                    if (dragStep.position < dropStep.position) {
                        start++
                    }
                }
                dropScenario.steps.splice(start, 0, dragStep)
            }
            // correct info
            dragScenario.steps.forEach(itemIndexAsPosition)
            dropScenario.steps.forEach(itemIndexAsPosition)
            dragStep.scenario_code = dropStep.scenario_code
            // update
            this.$forceUpdate()
            this.$store.dispatch(this.$at.scenario.POSITION_STEP, {
                botCode: this.$route.params.botCode,
                targetScenarioCode: dropScenario.code,
                stepCode: dragStep.code,
                stepCodeList: dropScenario.steps.map(step => step.code)
            })
        },
        updateSort () {
            switch (this.sortType) {
                case TreeSortType.CUSTOM:
                    this.scenarioList = lodash.orderBy(this.scenarios, item => item.position)
                    this.scenarioList.forEach(scenario => {
                        scenario.steps = lodash.orderBy(scenario.steps, item => item.position)
                    })
                    break
                case TreeSortType.NAME_ASC:
                    this.normalSort('name', 'asc')
                    break
                case TreeSortType.NAME_DSC:
                    this.normalSort('name', 'desc')
                    break
                case TreeSortType.TIME_ASC:
                    this.normalSort('created_time', 'asc')
                    break
                case TreeSortType.TIME_DSC:
                    this.normalSort('created_time', 'desc')
                    break
                default:
                    this.scenarioList = this.scenarios
            }
        },
        normalSort (property, direction) {
            this.scenarioList = lodash.orderBy(this.scenarios, property, direction)
            this.scenarioList.forEach(scenario => {
                scenario.steps = lodash.orderBy(scenario.steps, property, direction)
            })
        },
        createStep (scenario) {
            this.$appService.useEventBus(
                AppEventBusType.OPEN_STEP_EDITOR,
                { scenario }
            ).then(data => {
                this.$store.dispatch(this.$at.scenario.CREATE_STEP, data).then(step => {
                    this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                        activeStepCode: step.code
                    })
                })
            })
        },
        createScenario () {
            this.$appService.useEventBus(
                AppEventBusType.OPEN_SCENARIO_EDITOR,
                null
            ).then(data => {
                data.bot_code = this.$route.params.botCode
                this.$store.dispatch(this.$at.scenario.CREATE_SCENARIO, data).then(
                    () => {
                        this.$forceUpdate()
                    }
                )
            })
        },
        getStepCondition (step) {
            let condition = ''
            if (step.config.length > 0) {
                condition = step.config[0].config
                if (step.config.length > 1) {
                    condition += ` (+${step.config.length - 1})`
                }
            }
            return condition
        }
    },
    watch: {
        scenarios: {
            immediate: true,
            handler: function () {
                this.updateSort()
            }
        },
        sortType: {
            immediate: true,
            handler: function () {
                this.updateSort()
            }
        },
        shouldUpdateGraph () {
            this.$forceUpdate()
        },
        activeScenarioCode: {
            immediate: true,
            handler: function () {
                if (this.activeScenarioCode) {
                    this.scenarioOpenMap[this.activeScenarioCode] = true
                    this.$forceUpdate()
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
$borderColor: #E9EBF7;

.ScenarioTree {
    height: 100%;
    background: #fff;
    box-shadow: 0 3px 6px #00000029;
    overflow: hidden;
}

.scenario-list {
    overflow-y: auto;
    height: calc(100% - 107px);
    scroll-behavior: smooth;
}

.step-list {
    transition: all 0.3s ease;
    height: 0;
    overflow: hidden;
}

.item {
    min-height: 51px;
    border-top: 1px solid #fff;
    border-right: 1px solid #fff;
    border-bottom: 1px solid $borderColor;
    border-left: 5px solid #fff;
    display: flex;
    align-items: center;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: transform 0.3s ease;

    &.item-scenario {
        font-weight: bold;
    }

    &.item-step {
        min-height: 43px;
    }

    &.drop-zone {
        border-color: #0052CC;
        border-style: dashed dashed dashed solid;
    }

    &.active {
        .text-ellipsis {
            font-weight: bold;
            color: #0052CC;
        }
    }
}

.item-scenario {
    padding: 0 10px 0 20px;

    &.active {
        border-left-color: #0052CC;
    }
}

.item-step {
    padding: 0 10px 0 40px;
}

.v-icon {
    transition: all 0.3s ease;

    &.active {
        transform: rotate(180deg);
    }
}

.add-wrapper {
    background: #F2F4FA;
    padding: 10px;
    width: 260px;
}

.toolbar {
    display: flex;
    align-items: center;
    height: 51px;
    border-bottom: 1px solid $borderColor;
    width: 260px;

    &::v-deep .v-btn {
        margin: 0 5px;
    }
}
</style>
